import React, { useState, useEffect, useCallback } from 'react';
import { coachDashboardAPI } from '../services/api';
import LoadingScreen from './LoadingScreen';
import UserDetailView from './UserDetailView';
import styles from '../styles/CoachDashboard.module.css';

/**
 * CoachDashboard Component
 * Dashboard for coaches to manage their sessions, availability, and users
 *
 * Props:
 * - onBack: Callback to navigate back to main app
 */
function CoachDashboard({ onBack }) {
  const [activeTab, setActiveTab] = useState('sessions');
  const [profile, setProfile] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial vs. subsequent loads
  const [error, setError] = useState(null);
  const [isAddingAvailability, setIsAddingAvailability] = useState(false);
  const [newAvailability, setNewAvailability] = useState({
    dayOfWeek: 1, // Monday default
    startTime: '09:00',
    endTime: '17:00'
  });
  const [selectedUser, setSelectedUser] = useState(null); // For user detail view
  const [userSearchQuery, setUserSearchQuery] = useState(''); // Search filter for users

  // Day names for display
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setError(null);
      // Only show loading screen on initial load, not on subsequent refreshes
      if (isInitialLoad) {
        setIsLoading(true);
      }

      // Load profile first to verify coach status
      const profileData = await coachDashboardAPI.getMyProfile();
      setProfile(profileData.coach);

      // Load other data in parallel
      const [todayData, upcomingData, pastData, availData, usersData, statsData] = await Promise.all([
        coachDashboardAPI.getTodaySessions().catch(() => ({ sessions: [] })),
        coachDashboardAPI.getUpcomingSessions().catch(() => ({ sessions: [] })),
        coachDashboardAPI.getPastSessions().catch(() => ({ sessions: [] })),
        coachDashboardAPI.getMyAvailability().catch(() => ({ availability: [] })),
        coachDashboardAPI.getMyUsers().catch(() => ({ users: [] })),
        coachDashboardAPI.getMyStats().catch(() => ({ stats: null }))
      ]);

      setTodaySessions(todayData.sessions || []);
      setUpcomingSessions(upcomingData.sessions || []);
      setPastSessions(pastData.sessions || []);
      setAvailability(availData.availability || []);
      setUsers(usersData.users || []);
      setStats(statsData.stats || null);

    } catch (err) {
      console.error('[CoachDashboard] Error loading data:', err);
      if (err.message.includes('Not authorized') || err.message.includes('not a coach')) {
        setError('You are not registered as a coach.');
      } else {
        setError(err.message || 'Failed to load dashboard data');
      }
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
        setIsInitialLoad(false); // Mark initial load as complete
      }
    }
  }, [isInitialLoad]);

  useEffect(() => {
    loadData();

    // Refresh session list every minute
    const refreshInterval = setInterval(() => {
      loadData();
    }, 60000); // 60 seconds

    return () => clearInterval(refreshInterval);
  }, [loadData]);

  // Format session time
  const formatSessionTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get session timing state
  const getSessionTimingState = (sessionStart) => {
    const now = new Date();
    const start = new Date(sessionStart);
    const diffMs = start.getTime() - now.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    // Can cancel if more than 1 hour in future
    const canCancel = diffMinutes > 60;

    // Can join 30 minutes before to 10 minutes after
    const canJoin = diffMinutes >= -10 && diffMinutes <= 30;

    // Past join window (need status marking)
    const needsStatus = diffMinutes < -10;

    return { canCancel, canJoin, needsStatus, diffMinutes };
  };

  // Handle navigate to user in Users tab
  const handleNavigateToUser = (user) => {
    setActiveTab('users');
    setSelectedUser(user);
  };

  // Handle complete session
  const handleCompleteSession = async (sessionId) => {
    if (!window.confirm('Mark this session as completed?')) return;
    try {
      await coachDashboardAPI.completeSession(sessionId);
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error completing session:', err);
      alert('Failed to complete session: ' + err.message);
    }
  };

  // Handle no-show
  const handleNoShow = async (sessionId) => {
    if (!window.confirm('Mark this session as no-show?')) return;
    try {
      await coachDashboardAPI.markNoShow(sessionId);
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error marking no-show:', err);
      alert('Failed to mark no-show: ' + err.message);
    }
  };

  // Handle cancel session
  const handleCancelSession = async (sessionId) => {
    const reason = window.prompt('Reason for cancellation (optional):');
    if (reason === null) return; // User clicked cancel
    try {
      await coachDashboardAPI.cancelSession(sessionId, reason || null);
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error cancelling session:', err);
      alert('Failed to cancel session: ' + err.message);
    }
  };

  // Handle add availability
  const handleAddAvailability = async (e) => {
    e.preventDefault();
    try {
      // Get coach's timezone from browser
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await coachDashboardAPI.addAvailability(
        newAvailability.dayOfWeek,
        newAvailability.startTime,
        newAvailability.endTime,
        timezone
      );
      setIsAddingAvailability(false);
      setNewAvailability({ dayOfWeek: 1, startTime: '09:00', endTime: '17:00' });
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error adding availability:', err);
      alert('Failed to add availability: ' + err.message);
    }
  };

  // Handle delete availability
  const handleDeleteAvailability = async (availabilityId) => {
    if (!window.confirm('Delete this availability slot?')) return;
    try {
      await coachDashboardAPI.deleteAvailability(availabilityId);
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error deleting availability:', err);
      alert('Failed to delete availability: ' + err.message);
    }
  };

  // Handle toggle availability active status
  const handleToggleAvailability = async (slot) => {
    try {
      await coachDashboardAPI.updateAvailability(
        slot.id,
        slot.day_of_week,
        slot.start_time,
        slot.end_time,
        !slot.is_active
      );
      loadData(); // Refresh
    } catch (err) {
      console.error('[CoachDashboard] Error updating availability:', err);
      alert('Failed to update availability: ' + err.message);
    }
  };

  // Loading state
  if (isLoading) {
    return <LoadingScreen text="Loading coach dashboard..." variant="dots" />;
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Unable to Load Dashboard</h2>
          <p>{error}</p>
          <div className={styles.buttonGroup}>
            <button onClick={loadData} className={styles.retryButton}>
              Retry
            </button>
            <button onClick={onBack} className={styles.backButton}>
              Back to App
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButtonHeader} aria-label="Back">
          ← Back
        </button>
        <h1 className={styles.title}>Coach Dashboard</h1>
        {profile && (
          <div className={styles.profileBadge}>
            {profile.profileImage ? (
              <img src={profile.profileImage} alt="" className={styles.profileImage} />
            ) : (
              <div className={styles.profileImageFallback}>👤</div>
            )}
            <span>{profile.displayName}</span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'sessions' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Sessions
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'availability' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('availability')}
        >
          Availability
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'users' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'stats' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Stats
        </button>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className={styles.sessionsTab}>
            {/* Today's Sessions */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Today's Sessions</h2>
              {todaySessions.length === 0 ? (
                <p className={styles.emptyText}>No sessions scheduled for today.</p>
              ) : (
                <div className={styles.sessionsList}>
                  {todaySessions.map(session => {
                    const { canJoin, needsStatus } = getSessionTimingState(session.scheduled_start);
                    const userObj = users.find(u => u.id === session.user_id);

                    // Debug log
                    console.log('Today session:', session.id, 'has zoom_start_url:', session.zoom_start_url, 'canJoin:', canJoin);

                    return (
                      <div key={session.id} className={styles.sessionCard}>
                        <div className={styles.sessionInfo}>
                          <div className={styles.sessionTime}>
                            {formatSessionTime(session.scheduled_start)}
                          </div>
                          <div
                            className={styles.sessionUser}
                            onClick={() => userObj && handleNavigateToUser(userObj)}
                            style={{ cursor: userObj ? 'pointer' : 'default', color: userObj ? '#1b4942' : 'inherit' }}
                            title={userObj ? 'View user details' : ''}
                          >
                            {session.user_name || 'User'}
                          </div>
                          <div className={styles.sessionStatus}>
                            {session.status}
                          </div>
                        </div>
                        <div className={styles.sessionActions}>
                          {session.zoom_start_url ? (
                            <a
                              href={session.zoom_start_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.joinButton}
                            >
                              Join
                            </a>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#999' }}>No Zoom URL</span>
                          )}
                          {needsStatus && session.status === 'scheduled' && (
                            <>
                              <button
                                className={styles.completeButton}
                                onClick={() => handleCompleteSession(session.id)}
                              >
                                Complete
                              </button>
                              <button
                                className={styles.noShowButton}
                                onClick={() => handleNoShow(session.id)}
                              >
                                No-Show
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Upcoming Sessions */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Upcoming Sessions</h2>
              {upcomingSessions.length === 0 ? (
                <p className={styles.emptyText}>No upcoming sessions.</p>
              ) : (
                <div className={styles.sessionsList}>
                  {upcomingSessions.slice(0, 10).map(session => {
                    const { canCancel } = getSessionTimingState(session.scheduled_start);
                    const userObj = users.find(u => u.id === session.user_id);

                    // Debug log
                    console.log('Session:', session.id, 'has zoom_start_url:', session.zoom_start_url);

                    return (
                      <div key={session.id} className={styles.sessionCard}>
                        <div className={styles.sessionInfo}>
                          <div className={styles.sessionDate}>
                            {formatDate(session.scheduled_start)}
                          </div>
                          <div className={styles.sessionTime}>
                            {formatSessionTime(session.scheduled_start)}
                          </div>
                          <div
                            className={styles.sessionUser}
                            onClick={() => userObj && handleNavigateToUser(userObj)}
                            style={{ cursor: userObj ? 'pointer' : 'default', color: userObj ? '#1b4942' : 'inherit' }}
                            title={userObj ? 'View user details' : ''}
                          >
                            {session.user_name || 'User'}
                          </div>
                        </div>
                        <div className={styles.sessionActions}>
                          {session.zoom_start_url ? (
                            <a
                              href={session.zoom_start_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.joinButton}
                            >
                              Join
                            </a>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#999' }}>No Zoom URL</span>
                          )}
                          {canCancel && (
                            <button
                              className={styles.cancelButton}
                              onClick={() => handleCancelSession(session.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Past Sessions */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Past Sessions</h2>
              {pastSessions.length === 0 ? (
                <p className={styles.emptyText}>No past sessions.</p>
              ) : (
                <div className={styles.sessionsList}>
                  {pastSessions.slice(0, 20).map(session => {
                    const userObj = users.find(u => u.id === session.user_id);

                    return (
                      <div key={session.id} className={styles.sessionCard}>
                        <div className={styles.sessionInfo}>
                          <div className={styles.sessionDate}>
                            {formatDate(session.scheduled_start)}
                          </div>
                          <div className={styles.sessionTime}>
                            {formatSessionTime(session.scheduled_start)}
                          </div>
                          <div
                            className={styles.sessionUser}
                            onClick={() => userObj && handleNavigateToUser(userObj)}
                            style={{ cursor: userObj ? 'pointer' : 'default', color: userObj ? '#1b4942' : 'inherit' }}
                            title={userObj ? 'View user details' : ''}
                          >
                            {session.user_name || 'User'}
                          </div>
                          <div className={styles.sessionStatus}>
                            {session.status}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className={styles.availabilityTab}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Weekly Availability</h2>
              <p className={styles.sectionSubtitle}>
                Your recurring availability for coaching sessions. All times are in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
              </p>

              {/* Existing availability slots */}
              {availability.length === 0 ? (
                <p className={styles.emptyText}>No availability set. Add your available hours below.</p>
              ) : (
                <div className={styles.availabilityList}>
                  {availability
                    .sort((a, b) => a.day_of_week - b.day_of_week || a.start_time.localeCompare(b.start_time))
                    .map((slot) => (
                      <div key={slot.id} className={styles.availabilitySlot}>
                        <div className={styles.availabilityDay}>
                          {dayNames[slot.day_of_week]}
                        </div>
                        <div className={styles.availabilityTime}>
                          {slot.start_time} - {slot.end_time}
                        </div>
                        <div className={styles.availabilityStatus}>
                          <button
                            className={styles.toggleButton}
                            onClick={() => handleToggleAvailability(slot)}
                          >
                            {slot.is_active ? '✓ Active' : '○ Inactive'}
                          </button>
                        </div>
                        <button
                          className={styles.deleteButton}
                          onClick={() => handleDeleteAvailability(slot.id)}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                </div>
              )}

              {/* Add availability form */}
              {!isAddingAvailability ? (
                <button
                  className={styles.addButton}
                  onClick={() => setIsAddingAvailability(true)}
                >
                  + Add Availability
                </button>
              ) : (
                <form className={styles.availabilityForm} onSubmit={handleAddAvailability}>
                  <div className={styles.formRow}>
                    <label>
                      Day:
                      <select
                        value={newAvailability.dayOfWeek}
                        onChange={(e) => setNewAvailability({
                          ...newAvailability,
                          dayOfWeek: parseInt(e.target.value)
                        })}
                        className={styles.formSelect}
                      >
                        {dayNames.map((day, index) => (
                          <option key={index} value={index}>{day}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Start Time:
                      <input
                        type="time"
                        value={newAvailability.startTime}
                        onChange={(e) => setNewAvailability({
                          ...newAvailability,
                          startTime: e.target.value
                        })}
                        className={styles.formInput}
                        required
                      />
                    </label>
                    <label>
                      End Time:
                      <input
                        type="time"
                        value={newAvailability.endTime}
                        onChange={(e) => setNewAvailability({
                          ...newAvailability,
                          endTime: e.target.value
                        })}
                        className={styles.formInput}
                        required
                      />
                    </label>
                  </div>
                  <div className={styles.formActions}>
                    <button type="submit" className={styles.saveButton}>
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => {
                        setIsAddingAvailability(false);
                        setNewAvailability({ dayOfWeek: 1, startTime: '09:00', endTime: '17:00' });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </section>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className={styles.usersTab}>
            {selectedUser ? (
              <UserDetailView
                user={selectedUser}
                onBack={() => setSelectedUser(null)}
              />
            ) : (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Your Users</h2>
                <p className={styles.sectionSubtitle}>
                  Users who have booked sessions with you. Click to view their journey.
                </p>

                {/* Search input */}
                {users.length > 0 && (
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>
                )}

                {users.length === 0 ? (
                  <p className={styles.emptyText}>No users have booked sessions yet.</p>
                ) : (
                  (() => {
                    const filteredUsers = users.filter(user => {
                      const searchLower = userSearchQuery.toLowerCase();
                      const userName = (user.name || '').toLowerCase();
                      const userEmail = (user.email || '').toLowerCase();
                      return userName.includes(searchLower) || userEmail.includes(searchLower);
                    });

                    return filteredUsers.length === 0 ? (
                      <p className={styles.emptyText}>No users match your search.</p>
                    ) : (
                      <div className={styles.usersList}>
                        {filteredUsers.map(user => (
                          <div
                            key={user.id}
                            className={styles.userCard}
                            onClick={() => setSelectedUser(user)}
                            role="button"
                            tabIndex={0}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedUser(user);
                              }
                            }}
                          >
                            <div className={styles.userInfo}>
                              <div className={styles.userName}>{user.name || user.email}</div>
                              <div className={styles.userEmail}>{user.email}</div>
                            </div>
                            <div className={styles.userStats}>
                              <span>{user.session_count || 0} sessions</span>
                              <span className={styles.viewArrow}>→</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </section>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className={styles.statsTab}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Your Stats</h2>
              {stats ? (
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.total_sessions || 0}</div>
                    <div className={styles.statLabel}>Total Sessions</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.completed_sessions || 0}</div>
                    <div className={styles.statLabel}>Completed</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.cancelled_sessions || 0}</div>
                    <div className={styles.statLabel}>Cancelled</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.unique_users || 0}</div>
                    <div className={styles.statLabel}>Unique Users</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.this_week || 0}</div>
                    <div className={styles.statLabel}>This Week</div>
                  </div>
                  <div className={styles.statCard}>
                    <div className={styles.statValue}>{stats.this_month || 0}</div>
                    <div className={styles.statLabel}>This Month</div>
                  </div>
                </div>
              ) : (
                <p className={styles.emptyText}>No statistics available yet.</p>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoachDashboard;
